import { defineStore } from 'pinia'
import { TELEGRAM_BOT_USERNAME, TELEGRAM_FLOW_ID } from '@/config/env'
import { checkTelegramLinkApi } from '@/entities/personal/api/personalApi'
import { useAuthStore } from '@/stores/auth'

export const useNotificationChannelsStore = defineStore('notificationChannels', {
  state: () => ({
    isOpen: false,
    isTelegramLinkOpen: false,
    isChecking: false,
    isCheckingTelegramLink: false,
    telegramCheckError: null,
  }),

  getters: {
    telegramLinked() {
      return Boolean(useAuthStore().user?.telegramLinked)
    },
    telegramBotUrl() {
      const userId = useAuthStore().user?.id
      const base = `https://tg.pulse.is/${TELEGRAM_BOT_USERNAME}`
      if (!userId) {
        return base
      }
      return `${base}?start=${encodeURIComponent(TELEGRAM_FLOW_ID)}&web_id=${encodeURIComponent(String(userId))}`
    },
  },

  actions: {
    close() {
      this.isOpen = false
    },

    openTelegramLink() {
      this.telegramCheckError = null
      this.isOpen = false
      this.isTelegramLinkOpen = true
    },

    closeTelegramLink() {
      this.isTelegramLinkOpen = false
      this.telegramCheckError = null
    },

    openTelegramBot() {
      const url = this.telegramBotUrl
      if (!url) return
      window.open(url, '_blank', 'noopener,noreferrer')
    },

    async promptIfUnlinked({ refresh = true } = {}) {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn || authStore.user?.role !== 'client') {
        return false
      }

      if (this.isChecking) {
        return false
      }

      this.isChecking = true
      try {
        if (refresh) {
          try {
            await authStore.refreshPersonalProfile()
          } catch (error) {
            console.warn('[notification-channels] profile refresh failed', error)
          }
        }

        if (!this.telegramLinked) {
          this.isOpen = true
          return true
        }

        this.isOpen = false
        return false
      } finally {
        this.isChecking = false
      }
    },

    async checkTelegramLink() {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        return false
      }

      this.isCheckingTelegramLink = true
      this.telegramCheckError = null
      try {
        const payload = await checkTelegramLinkApi()
        const linkedFromResponse = Boolean(
          payload?.data?.linked ?? payload?.data?.sp_tg_linked,
        )

        try {
          await authStore.refreshPersonalProfile()
        } catch (error) {
          console.warn('[notification-channels] profile refresh after check failed', error)
        }

        if (this.telegramLinked || linkedFromResponse) {
          this.isTelegramLinkOpen = false
          this.isOpen = false
          return true
        }

        this.telegramCheckError = 'not_linked'
        return false
      } catch (error) {
        this.telegramCheckError = error?.code || 'check_failed'
        throw error
      } finally {
        this.isCheckingTelegramLink = false
      }
    },

    clear() {
      this.isOpen = false
      this.isTelegramLinkOpen = false
      this.isChecking = false
      this.isCheckingTelegramLink = false
      this.telegramCheckError = null
    },
  },
})
